import { DvmModule, MapViewerService, NavigableMinimapComponent } from '@3ddv/ngx-dvm-internal';
import { AfterViewInit, Component, computed, DestroyRef, effect, EffectRef, ElementRef, inject, signal, Signal, untracked, ViewChild, WritableSignal } from '@angular/core';
import { APP_CONFIG } from '@configuration/configuration';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Configuration } from '@models/interfaces/configuration/index.interface';
import { MenuService } from '@services/components/menu.service';
import { MinimapService } from '@services/components/minimap.service';
import { DvmService } from '@services/dvm.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import anime from 'animejs';
import { ViewerRouteParamsService } from '@services/viewer-route-params.service';

@Component({
    selector: 'app-minimap',
    imports: [ DvmModule ],
    standalone: true,
    templateUrl: './minimap.component.html',
    styleUrl: './minimap.component.css'
})
export class MinimapComponent implements AfterViewInit{

  // VIEWCHILDS
  @ViewChild('minimapContainer')
  private readonly minimapContainer!: ElementRef<HTMLElement>
  
  @ViewChild('navigableMinimap', { static: false })
  public readonly  navigableMinimap!: NavigableMinimapComponent;
  
  // SERVICES
  private   readonly config:          Signal<Configuration> = inject(APP_CONFIG);
  private   readonly destroyRef:      DestroyRef            = inject(DestroyRef);
  public    readonly dvmService:      DvmService            = inject(DvmService);
  private   readonly menuService:     MenuService           = inject(MenuService);
  private   readonly minimapService:  MinimapService        = inject(MinimapService);
  private   readonly hasViewId:       boolean              = inject(ViewerRouteParamsService).hasViewId;
  
  // GLOBAL STATE
  protected readonly view:            Signal<AppViews|null>     = this.dvmService.view;
  protected readonly minimap:         MapViewerService          = this.dvmService.minimap;
  protected readonly enableMinimap:   Signal<boolean>           = computed((): boolean => this.config()['Dvm']['minimap'].enabled);
  protected readonly navigableMinimapEnabled:   Signal<boolean> = computed((): boolean => this.config()['Dvm']['minimap'].navigableMinimapEnabled);

  // APP VIEWS
  protected readonly appViews:        typeof AppViews           = AppViews;  
  
  // COMPONENT STATE
  protected readonly showMinimap:   WritableSignal<boolean>     = signal(false);
  protected readonly useNavigable:  WritableSignal<boolean>     = signal(false);
  private minimapAnimation: anime.AnimeInstance | null = null;
  private visibilityTarget: boolean = false;

  // GETTERS
  get isLoaded() {
    return this.navigableMinimap.service.isLoaded();
  }

  get isMinimapHidden(): boolean {
    return this.hasViewId || !this.showMinimap() || !this.enableMinimap();
  }

  // EFFECTS
  private  readonly onViewChange:      EffectRef = effect((): void => {    

    const view: AppViews | null   = this.view();
    let navigableEnabled: boolean = this.navigableMinimapEnabled();
    
    // Si tiene un view_id, no se hace nada
    if(!view || this.hasViewId) return;
    
    // Si la vista es Main y el minimapa navegable esta habilitado, se habilita el minimapa navegable
    if(view === this.appViews.Main && untracked(this.enableMinimap)) navigableEnabled = true;
        
    this.useNavigable.set(navigableEnabled); // use navigable map if enabled

  });

  private readonly onVisibilityChange: EffectRef = effect((): void => {
    
    const show: boolean = this.shouldShowMinimap();
    
    this.animateVisibility(show);

  });

  // LIFECYCLE       
  ngAfterViewInit(): void {

    // Usamos shouldShowMinimap() y no el estado crudo del servicio: la visibilidad persiste en el
    // servicio root y no implica que esta instancia deba mostrarse (vista, minimapa cargado, etc.)
    this.animateVisibility(this.shouldShowMinimap());

    // LISTEN NAVIGABLE MINIMAP SERVICE INITIALIZATION
    this.navigableMinimap.service.waitInitialize().pipe(
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((): void => {    
      
      // LOAD SUCCESS
      this.navigableMinimap.service.getObservable('load_success').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => {
        
        if(this.useNavigable()) this.minimapService.setVisibility(true);
        
      });

      // RESET
      this.navigableMinimap.service.getObservable('reset').pipe(
        takeUntilDestroyed(this.destroyRef)
      ).subscribe((): void => {

        if(!this.useNavigable() && !this.hasViewId) this.minimapService.setVisibility(true);       
         
      });

    });

  }
 
  // METHODS
  private shouldShowMinimap(): boolean {

    const view: AppViews | null = this.view();

    if(
      this.hasViewId ||
      !this.minimapService.isVisible() ||
      !this.enableMinimap()
    ) return false;

    if(this.useNavigable()) return this.isLoaded;

    return view !== this.appViews.Topview && this.minimap.isLoaded();

  }

  private animateVisibility(show: boolean): void {

    const container: HTMLElement | undefined = this.minimapContainer?.nativeElement;

    if(!container) return;

    this.visibilityTarget = show;

    if(show && !this.showMinimap()) {
      requestAnimationFrame((): void => {
        if(!this.visibilityTarget) return;
        container.style.opacity = '0';
        container.style.transform = 'translateY(20px)';
        this.showMinimap.set(true);
        requestAnimationFrame((): void => {
          requestAnimationFrame((): void => this.runVisibilityAnimation(true));
        });
      });
      return;
    }

    this.runVisibilityAnimation(show);

  }

  private runVisibilityAnimation(show: boolean): void {

    const container: HTMLElement | undefined = this.minimapContainer?.nativeElement;

    if(!container) return;

    this.minimapAnimation?.pause();
    anime.remove(container);

    this.minimapAnimation = anime({
      targets:  container,
      opacity:  show ? 1 : 0,
      translateY: show ? 0 : 20,
      duration: 300,
      easing:   'easeInOutQuad',
      complete: (): void => {
        if(!show && !this.visibilityTarget) this.showMinimap.set(false);
      }
    });

  }

  public onMinimapClick(): void {

    // Si la vista es Main, no hacemos nada
    if(this.view() === AppViews.Main || this.useNavigable()) return; 

    // Forzamos el seteo null de un menu item y asi limpiamos filtros y ams si hay
    this.menuService.setMenuItem(null);

    // Reseteamos el viewer
    this.dvmService.resetViewer();

  }
  
}
